<template>
    <div v-if="isLoading" class="d-flex align-items-center" style="gap: 5px">
        <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
        <span>{{ $t('components.datatable.index.loadingText') }}</span>
    </div>
    <div v-else id="calculator-tab" class="row">
        <div class="col-xs-12 col">
            <table class="table table-bordered modal-table">
                <thead class="thead-light">
                    <tr>
                        <th>{{ $t('views.inventory.modals.productDetail.calculator.columns.title') }}</th>
                        <th>{{ $t('views.inventory.modals.productDetail.calculator.columns.price') }}</th>
                        <th>{{ $t('views.inventory.modals.productDetail.calculator.columns.calculate') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{{ $t('views.inventory.modals.productDetail.calculator.salePrice') }}</td>
                        <td>{{ calculate.calculated?.targetCurrency }} {{ calculate.calculated?.sales_price ? formatNumber(calculate.calculated?.sales_price) : '-' }}</td>
                        <td class="pt-0 pb-0 d-flex align-items-center">
                            <div class="input-group">
                                <CurrencyInput v-model="form.sales_price" :placeholder="$t('views.inventory.modals.productDetail.calculator.enterPrice')" class="sm-input form-control" @blur="handleCalculate" />
                                <span class="input-group-text" style="background-color: #fff !important; max-height: 37px">{{ calculate.calculated?.targetCurrency }}</span>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td>{{ $t('views.inventory.modals.productDetail.calculator.productCost') }}</td>
                        <td>{{ calculate.calculated?.sourceCurrency }} {{ calculate.calculated?.cost_of_goods ? formatNumber(calculate.calculated?.cost_of_goods) : '-' }}</td>
                        <td class="pt-0 pb-0 d-flex align-items-center">
                            <div class="input-group">
                                <CurrencyInput v-model="form.cost_of_goods" :placeholder="$t('views.inventory.modals.productDetail.calculator.enterPrice')" class="sm-input form-control" @blur="handleCalculate" />
                                <span class="input-group-text" style="background-color: #fff !important; max-height: 37px">{{ calculate.calculated?.sourceCurrency }}</span>
                            </div>
                        </td>
                    </tr>
                    <tr style="cursor: pointer" @click="isShowFeeDetail = !isShowFeeDetail">
                        <td>
                            <div class="d-flex align-items-center" style="gap: 10px">
                                <i :class="isShowFeeDetail ? 'bi-dash-lg' : 'bi-plus-lg'"></i>
                                <span>{{ $t('views.inventory.modals.productDetail.calculator.taxFee') }}</span>
                            </div>
                        </td>
                        <td>{{ calculate.calculated?.targetCurrency }} {{ calculate.calculated?.total_fee ? formatNumber(calculate.calculated?.total_fee) : '-' }}</td>
                        <td>{{ calculate.custom?.targetCurrency }} {{ calculate.custom?.total_fee ? formatNumber(calculate.custom?.total_fee) : '-' }}</td>
                    </tr>
                    <template v-if="isShowFeeDetail">
                        <tr class="bg-light">
                            <td style="padding-left: 38px">{{ $t('views.inventory.modals.productDetail.calculator.referralFee') }}</td>
                            <td>{{ calculate.calculated?.targetCurrency }} {{ calculate.calculated?.referral_fee ? formatNumber(calculate.calculated?.referral_fee) : '-' }}</td>
                            <td>{{ calculate.custom?.targetCurrency }} {{ calculate.custom?.referral_fee ? formatNumber(calculate.custom?.referral_fee) : '-' }}</td>
                        </tr>
                        <tr class="bg-light">
                            <td style="padding-left: 38px">{{ $t('views.inventory.modals.productDetail.calculator.fbaFee') }}</td>
                            <td>{{ calculate.calculated?.targetCurrency }} {{ calculate.calculated?.fba_fee ? formatNumber(calculate.calculated?.fba_fee) : '-' }}</td>
                            <td>{{ calculate.custom?.targetCurrency }} {{ calculate.custom?.fba_fee ? formatNumber(calculate.custom?.fba_fee) : '-' }}</td>
                        </tr>
                        <tr class="bg-light">
                            <td style="padding-left: 38px">{{ $t('views.inventory.modals.productDetail.calculator.storageFee') }}</td>
                            <td>{{ calculate.calculated?.targetCurrency }} {{ calculate.calculated?.storage_fee ? formatNumber(calculate.calculated?.storage_fee) : '-' }}</td>
                            <td>{{ calculate.calculated?.targetCurrency }} {{ calculate.calculated?.storage_fee ? formatNumber(calculate.calculated?.storage_fee) : '-' }}</td>
                        </tr>
                    </template>
                    <tr>
                        <td>{{ $t('views.inventory.modals.productDetail.calculator.profit') }}</td>
                        <td>{{ calculate.calculated?.targetCurrency }} {{ calculate.calculated?.profit ? formatNumber(calculate.calculated?.profit) : '-' }}</td>
                        <td>{{ calculate.custom?.targetCurrency }} {{ calculate.custom?.profit ? formatNumber(calculate.custom?.profit) : '-' }}</td>
                    </tr>
                    <tr>
                        <td>{{ $t('views.inventory.modals.productDetail.calculator.roi') }}</td>
                        <td :class="isHyphenClass(calculate.calculated?.roi)">{{ formatPercentage(calculate.calculated?.roi) }}</td>
                        <td :class="isHyphenClass(calculate.custom?.roi)">{{ formatPercentage(calculate.custom?.roi) }}</td>
                    </tr>
                    <tr>
                        <td>{{ $t('views.inventory.modals.productDetail.calculator.profitMargin') }}</td>
                        <td :class="isHyphenClass(calculate.calculated?.profit_margin)">{{ formatPercentage(calculate.calculated?.profit_margin) }}</td>
                        <td :class="isHyphenClass(calculate.custom?.profit_margin)">{{ formatPercentage(calculate.custom?.profit_margin) }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div class="col-xs-12 col sm-max-w-100" style="max-width: 320px">
            <table class="table table-bordered modal-table">
                <tbody>
                    <tr>
                        <td style="width: 158px; border-right: none !important">{{ $t('views.inventory.modals.productDetail.calculator.lowestPrice') }}</td>
                        <td>{{ calculate.calculated?.currency }} {{ calculate?.lowest_price ? formatNumber(calculate?.lowest_price) : '-' }}</td>
                    </tr>
                    <tr>
                        <td style="width: 158px; border-right: none !important">{{ $t('views.inventory.modals.productDetail.calculator.buyboxPrice') }}</td>
                        <td>{{ calculate.calculated?.currency }} {{ calculate?.buybox_price ? formatNumber(calculate?.buybox_price) : '-' }}</td>
                    </tr>
                    <tr>
                        <td style="width: 158px; border-right: none !important">{{ $t('views.inventory.modals.productDetail.calculator.currentCost') }}</td>
                        <td>
                            <div class="d-flex align-items-center" style="gap: 5px">
                                <span> {{ currentCalculate.total ? `${currentCalculate?.currency} ${formatNumber(currentCalculate.total)}` : '' }} </span>
                                <button
                                    class="btn btn-link text-secondary dropdown-toggle d-flex"
                                    type="button"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                    style="background-color: var(--bs-gray-200); font-size: 11px; padding: 0.3rem 0.5rem; gap: 5px"
                                    data-bs-dropdown-animation
                                >
                                    <template v-if="selectedTargetCountry">
                                        <img class="svg-flag" :src="require(`@/assets/svg-flag/${selectedTargetCountry}.svg`)" width="16" height="16" alt="" /> <span>{{ selectedTargetCountry }}</span>
                                    </template>
                                    <template v-else> {{ $t('actions.change') }}</template>
                                </button>
                                <div class="dropdown-menu p-0" style="width: 85px; min-width: 85px; max-height: 200px; overflow: auto">
                                    <button type="button" v-for="item in getShortFromMarket" :key="item" class="dropdown-item with-border d-flex align-items-center" style="gap: 5px" @click="changeTargetCountry(item)">
                                        <img class="svg-flag" :src="require(`@/assets/svg-flag/${item}.svg`)" width="16" height="16" alt="" /> <span>{{ item }}</span>
                                    </button>
                                </div>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td class="text-muted" style="width: 158px; border-right: none !important; padding-left: 25px">{{ $t('views.inventory.modals.productDetail.calculator.currentProduct') }}</td>
                        <td>{{ currentCalculate?.source ? `${currentCalculate?.currency} ${formatNumber(currentCalculate?.source)}` : '-' }}</td>
                    </tr>
                    <tr>
                        <td class="text-muted" style="width: 158px; border-right: none !important; padding-left: 25px">{{ $t('views.inventory.modals.productDetail.calculator.warehouse') }}</td>
                        <td>
                            {{ currentCalculate?.warehouse ? `${currentCalculate?.currency} ${formatNumber(currentCalculate?.warehouse)}` : '-' }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import moment from 'moment'
import { countryInfo } from '@/utils/helper'
import CurrencyInput from '@/components/CurrencyInput.vue'
import { formatPercentage } from '@/utils/format-number'

const countries = countryInfo()

export default {
    name: 'ProductDetailCalculatorModal',
    components: { CurrencyInput },
    data() {
        return {
            countries,
            moment,
            form: {
                sales_price: null,
                cost_of_goods: null,
            },
            isShowFeeDetail: false,
            selectedTargetCountry: null,
            isLoading: true,
            formatPercentage,
        }
    },
    computed: {
        ...mapState('inventory', ['calculate', 'currentCalculate']),
        ...mapGetters('market', ['markets', 'currentMarket']),
        getShortFromMarket() {
            const currentMarketShort = this.markets.find((market) => market.short === this.currentMarket.short)
            return currentMarketShort.sources.map((market) => market.source)
        },
    },
    created() {
        this.setCalculate().finally(() => (this.isLoading = false))
    },
    methods: {
        ...mapActions('inventory', ['setCalculate', 'getCurrentCalculate']),
        formatNumber(number) {
            if (number === null || number === undefined) return null

            const factor = Math.pow(10, 2)
            const rounded = Math.floor(number * factor + 1e-9) / factor
            return rounded.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
        },
        handleCalculate(e) {
            const { value } = e.target
            if (!value) return
            this.setCalculate(this.form)
        },
        changeTargetCountry(source) {
            this.selectedTargetCountry = source
            this.getCurrentCalculate({ source })
        },
        isHyphenClass(value) {
            if (value === 0) {
                return 'text-muted'
            } else if (value < 0) {
                return 'text-danger'
            }
            return 'text-success'
        },
    },
    watch: {},
}
</script>

<style lang="scss" scoped>
[id^='calculator-tab'] {
    .modal-table {
        margin-bottom: 0 !important;
        border-radius: 5px;

        th {
            height: 48px;
        }

        &:first-child {
            td:first-child {
                width: 260px;
            }
        }

        &:last-child {
            td:first-child {
                width: 260px;
            }
        }

        tbody {
            tr {
                height: 48px;

                td {
                    font-size: 12px;
                    line-height: 21px;
                    color: #677788;
                    height: 48px;
                    font-weight: 400;
                    width: 240px;
                }
                td:first-child {
                    font-weight: 600;
                }
                td:nth-child(odd) {
                    border-right: 1px solid rgba(231, 234, 243, 0.6980392157);
                }
                td:nth-child(even) {
                    font-weight: 400;
                    color: #677788;
                }
            }
        }

        .text-ellipsis {
            font-size: 11.9px;
            font-weight: 400;
            line-height: 17.9px;
            color: #677788;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
        }
    }

    .form-control {
        font-size: 12px;
        font-weight: 400;
        height: 37px;

        &::placeholder {
            color: #67778880;
        }
    }
}
</style>
